import cx from 'classnames';
import { find, isEmpty, toLower } from 'lodash';
import React, { useMemo, Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';

import {
  Button,
  CountdownTimer,
  Deeplinks,
  PaymentFields,
  PaymentLayout,
  PaymentLayoutAction,
} from 'components';
import { PaymentDetailsRules } from 'components/PaymentDetailsRules';
import {
  COPY_ICON_PATH,
  EXCLAMATION_CLOUD_ICON_PATH,
} from 'constants/images.constants';
import { PaymentType, EventOptionPaymentStepName } from 'enums';
import { useOrderContext, useTrackPaymentStepDuration } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Currency, Order, TradeMethod } from 'types';

import { useDeeplinks } from './useDeeplinks';
import './PaymentDetails.style.scss';

type Props = {
  order: Order;
  currencies: Array<Currency>;
  refetchOrderStatus: () => void;
  actions: PaymentLayoutAction[];
  tradeMethod: TradeMethod;
};

export const PaymentDetails: React.FC<Props> = ({
  order,
  currencies,
  refetchOrderStatus,
  actions,
  tradeMethod,
}) => {
  useTrackPaymentStepDuration(
    EventOptionPaymentStepName.CustomerConfirmTransfer,
  );

  const [changeAmountConfirmed, setChangeAmountConfirmed] = useState(
    order.amount === order.initialAmount,
  );

  const { isTSBP, isSIM, customerBank, customerBankName } = useOrderContext();

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });
  const { requisites, amount, currency } = useMemo(() => order || {}, [order]);
  const currencySymbol = useMemo(
    () => find(currencies, ['code', currency])?.symbol || '',
    [currency, currencies],
  );

  const countryCode = useMemo(
    () => requisites?.countryCode || '',
    [requisites?.countryCode],
  );

  const requisitesCountry = useMemo(
    () =>
      t(`country.values.${toLower(countryCode)}`, {
        defaultValue: requisites?.countryNameEn || countryCode,
      }),
    [countryCode, requisites?.countryNameEn, t],
  );

  const isSberPay = useMemo(
    () => tradeMethod.paymentType === PaymentType.SberPay,
    [tradeMethod.paymentType],
  );

  const copyToClipboard = useCallback((value: string) => {
    navigator.clipboard.writeText(value);
  }, []);

  const renderTitle = useCallback(
    () => (
      <div className="tw-text-primary tw-text-lg md:tw-text-2xl">
        <span className="tw-font-semibold">
          {t(
            isTSBP ? 'rules_tsbp.title' : isSberPay ? 'open_sberpay' : 'open',
            { bank: customerBankName, country: requisitesCountry },
          )}
        </span>
        {isSIM ? (
          <span className="tw-text-secondary">{t('sim_top_up')}</span>
        ) : (
          !isTSBP && (
            <Fragment>
              <span className="tw-text-secondary">{t('transfer')}</span>
              {!isSberPay && (
                <span className="tw-font-semibold">{t('requisites')}</span>
              )}
            </Fragment>
          )
        )}
        <div
          className={cx('tw-mt-2 tw-flex tw-items-center tw-justify-between', {
            'tw-bg-layout-accent': !changeAmountConfirmed,
          })}
        >
          <span className="tw-font-bold">
            {`${t('amount')}:`}
            <span className="tw-bg-red-100 tw-text-red-800 tw-text-xl tw-font-bold tw-me-2 tw-px-2.5 tw-py-2.5 tw-rounded-sm tw-dark:bg-red-900 tw-dark:text-red-300 tw-mr-3 tw-ml-3 tw-mt-1 tw-mb-1 tw-inline-block">{` ${amount} ${currencySymbol} `}</span>
          </span>
          <Button
            className={cx(
              'tw-flex tw-justify-center tw-items-center tw-self-start !tw-w-10 !tw-text-sm !tw-text-copy !tw-rounded-lg !tw-h-8 tw-top-3 tw-right-2',
            )}
            color="tertiary"
            onClick={() => copyToClipboard(String(amount))}
          >
            <SVG
              src={COPY_ICON_PATH}
              title="copy"
              className="tw-w-3.5 tw-h-3.5"
            />
          </Button>
        </div>
      </div>
    ),
    [
      amount,
      currencySymbol,
      customerBankName,
      isSberPay,
      isTSBP,
      isSIM,
      requisitesCountry,
      changeAmountConfirmed,
      copyToClipboard,
      t,
    ],
  );

  const deeplinks = useDeeplinks({
    order,
    selectedTradeMethod: tradeMethod,
    customerBankCode: customerBank || '',
  });

  return (
    <PaymentLayout actions={changeAmountConfirmed ? actions : []}>
      {!changeAmountConfirmed && (
        <div className="tw-absolute tw-bg-black tw-top-0 tw-left-0 tw-right-0 tw-bottom-0 tw-opacity-70 tw-z-[1]"></div>
      )}
      <div className="tw-w-full">
        <div className="tw-flex tw-items-center tw-w-full tw-mb-4">
          <CountdownTimer
            order={order}
            refetchOrderStatus={refetchOrderStatus}
          />
          <div className="tw-relative tw-z-[3]">
            {renderTitle()}
            {!changeAmountConfirmed && (
              <div className="tw-absolute tw-w-full tw-p-4 tw-mt-4 tw-bg-layout-accent-alt tw-rounded-b-xl">
                <span className="tw-absolute tw-top-[-1.2rem] tw-left-8 tw-text-layout-accent-alt tw-text-2xl tw-scale-x-150">
                  {'▲'}
                </span>
                <div className="tw-text-lg md:tw-text-xl tw-mb-1 tw-font-semibold">
                  {t('change_amount.title')}
                </div>
                <div className="tw-text-lg md:tw-text-xl">
                  {t('change_amount.description')}
                </div>
                <div className="tw-mt-4 tw-w-32">
                  <Button
                    heightClassName="tw-h-10"
                    color="success"
                    onClick={() => setChangeAmountConfirmed(true)}
                  >
                    {t('change_amount.confirm')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <PaymentFields
          tradeMethod={tradeMethod}
          requisites={requisites}
          requisitesCountry={requisitesCountry}
          deeplinksExist={!isEmpty(deeplinks)}
        />
        {!isEmpty(deeplinks) && <Deeplinks deeplinks={deeplinks} />}
        <div className="tw-flex tw-items-start">
          <div className="tw-hidden tw-items-center tw-justify-center tw-min-w-24 tw-w-24 tw-h-24 tw-mr-8 tw-rounded-2xl tw-bg-layout-accent-alt md:tw-flex">
            <SVG
              src={EXCLAMATION_CLOUD_ICON_PATH}
              title="exclamation-icon"
              className="tw-w-9"
            />
          </div>
          <PaymentDetailsRules requisitesCountry={requisitesCountry} />
        </div>
      </div>
    </PaymentLayout>
  );
};
